import React, { useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { GridSelectionModel } from '@material-ui/data-grid';
import pluralize from 'pluralize';
import { uniqBy, groupBy } from 'lodash';
import { WaitlistVolunteerMenuButton, WaitlistActions } from './WaitlistVolunteerMenu';
import {
  useUnenrolWaitlist,
  useEnrolWaitlistingFetch,
  useEnrolBulkWaitlist,
  useUnenrolBulkWaitlist,
} from '../../activity-management/activity-form-v2/activity-form-actions-v2';
import { TemplateSnackbar, SnackBarOptions } from '../../../../common/snackbars/TemplateSnackbar';
import { WaitlistRemovingMessage } from './WaitlistRemovingMessage';
import {
  ConfirmAddMaxVolunteer,
  ITeam,
} from '../../activities/activity-timeline/past/report/form/ConfirmAddMaxVolunteer';
import { usePendingActivityRequests } from '../common/pending-requests/usePendingActivityRequests';
import { PendingRequestsFilterBar } from '../common/pending-requests/PendingRequestsFilterBar';
import { PendingRequestsGrid } from '../common/pending-requests/PendingRequestsGrid';
import { PendingRequestsHeader } from '../common/pending-requests/PendingRequestsHeader';
import { downloadPendingRequestsCsv } from '../common/pending-requests/pending-requests-csv';
import { PendingRequestRow, getVolunteerName } from '../common/pending-requests/pending-requests-types';

const toTeamMember = (row: PendingRequestRow) => ({
  activityId: row.activityId,
  activityName: row.activityName,
  volunteerId: row.volunteerId,
  volunteerName: getVolunteerName(row),
});

export function WaitlistVolunteersScreen() {
  const requests = usePendingActivityRequests('activity-waitlistings');
  const { rows, refetch } = requests;

  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [snackbar, setSnackbar] = useState<SnackBarOptions>();
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [teamExceed, setTeamExceed] = useState(false);
  const [exceedingTeams, setExceedingTeams] = useState<ITeam>();
  const maxTeamConfirm = useRef<() => void>();
  const maxTeamCancel = useRef<() => void>();

  const enrolWaitlisting = useEnrolWaitlistingFetch();
  const unenrolWaitlisting = useUnenrolWaitlist();
  const enrolBulkWaitlisting = useEnrolBulkWaitlist();
  const unenrolBulkWaitlisting = useUnenrolBulkWaitlist();

  const selectedIds = selectionModel as string[];
  const selectedRows = rows.filter((row) => selectedIds.includes(row.id));
  const selectedVolunteers = uniqBy(selectedRows, (row) => row.volunteerId);
  const selectedActivities = uniqBy(selectedRows, (row) => row.activityId);

  const volunteerText =
    selectedVolunteers.length === 1
      ? getVolunteerName(selectedVolunteers[0])
      : `${selectedVolunteers.length} volunteers`;
  const activityText =
    selectedActivities.length === 1 ? selectedActivities[0].activityName : `${selectedActivities.length} activities`;

  const onActionComplete = (message: string) => {
    setSelectionModel([]);
    refetch();
    setSnackbar({ open: true, variant: 'success', message });
  };

  const confirmIfTeamsExceeded = (teams: ITeam, onConfirm: () => void) => {
    if (Object.keys(teams).length === 0) {
      onConfirm();
      return;
    }
    setExceedingTeams(teams);
    setTeamExceed(true);
    maxTeamConfirm.current = () => {
      onConfirm();
      setTeamExceed(false);
      setExceedingTeams(undefined);
    };
    maxTeamCancel.current = () => setTeamExceed(false);
  };

  const addToTeam = (row: PendingRequestRow) => {
    const exceeds = row.maxTeam !== null && row.maxTeam > 0 && row.totalEnrolments >= row.maxTeam;
    confirmIfTeamsExceeded(exceeds ? { [row.activityId]: [toTeamMember(row)] } : {}, () =>
      enrolWaitlisting
        .run({ activityWaitlistingId: row.id })
        .then(() => onActionComplete('Added waitlisting to team successfully'))
    );
  };

  const bulkAddToTeam = () => {
    const byActivity = groupBy(selectedRows, (row) => row.activityId);
    const exceeding = Object.keys(byActivity).reduce<ITeam>((acc, activityId) => {
      const activityRows = byActivity[activityId];
      const { maxTeam, totalEnrolments } = activityRows[0];
      return maxTeam && maxTeam < activityRows.length + totalEnrolments
        ? { ...acc, [activityId]: activityRows.map(toTeamMember) }
        : acc;
    }, {});
    const count = selectedIds.length;

    confirmIfTeamsExceeded(exceeding, () =>
      enrolBulkWaitlisting
        .run({ activityWaitlistingIds: selectedIds })
        .then(() => onActionComplete(`Added ${count} waitlistings to team successfully`))
    );
  };

  const onWithdraw = (removalReason: string) => {
    const count = selectedIds.length;
    const request =
      count === 1
        ? unenrolWaitlisting.run({ activityWaitlistingId: selectedIds[0], removalReason })
        : unenrolBulkWaitlisting.run({ activityWaitlistingIds: selectedIds, removalReason });

    request.then(() => {
      setShowRemoveDialog(false);
      onActionComplete(count === 1 ? 'Removed waitlisting successfully' : `Removed ${count} waitlistings successfully`);
    });
  };

  const onAction = (action: WaitlistActions, row: PendingRequestRow) => {
    switch (action) {
      case 'add':
        addToTeam(row);
        break;
      case 'withdraw':
        setSelectionModel([row.id]);
        setShowRemoveDialog(true);
        break;
      default:
        break;
    }
  };

  const onActionBulk = (action: WaitlistActions) => {
    switch (action) {
      case 'add':
        bulkAddToTeam();
        break;
      case 'withdraw':
        setShowRemoveDialog(true);
        break;
      default:
        break;
    }
  };

  const onDownload = () => {
    requests
      .fetchAllRows()
      .then((allRows) => downloadPendingRequestsCsv(allRows, 'Date Added', 'volunteers_waitlists'))
      .catch(() => setSnackbar({ open: true, variant: 'error', message: 'Unable to download CSV' }));
  };

  return (
    <Box display='flex' flexDirection='column' flex={1} height='100%' overflow='hidden'>
      <PendingRequestsHeader
        total={requests.total}
        noun={pluralize('Activity Waitlisting', requests.total)}
        selectedCount={selectedRows.length}
        bulkActions={<WaitlistVolunteerMenuButton onAction={onActionBulk} bulk />}
        onDownload={onDownload}
        downloading={requests.downloading}
      />
      <Box display='flex' flexDirection='column' flex={1} overflow='auto' minHeight={0}>
        <PendingRequestsFilterBar
          filters={requests.filters}
          filterOptions={requests.filterOptions}
          onChange={(changes) => {
            setSelectionModel([]);
            requests.setFilters(changes);
          }}
          onReset={() => {
            setSelectionModel([]);
            requests.resetFilters();
          }}
          dateLabel='Date Added'
        />
        <PendingRequestsGrid
          rows={rows}
          total={requests.total}
          page={requests.page}
          onPageChange={(page) => {
            setSelectionModel([]);
            requests.setPage(page);
          }}
          loading={requests.loading}
          error={requests.error}
          onRetry={refetch}
          orderBy={requests.orderBy}
          orderAscending={requests.orderAscending}
          onSort={(field) => {
            setSelectionModel([]);
            requests.toggleSort(field);
          }}
          dateLabel='Date Added'
          selectionModel={selectionModel}
          onSelectionModelChange={setSelectionModel}
          renderActions={(row) => (
            <WaitlistVolunteerMenuButton onAction={(action: WaitlistActions) => onAction(action, row)} bulk={false} />
          )}
        />
      </Box>
      {snackbar && <TemplateSnackbar {...snackbar} onClose={() => setSnackbar(undefined)} />}
      <WaitlistRemovingMessage
        open={selectedRows.length > 0 && showRemoveDialog}
        onClose={() => setShowRemoveDialog(false)}
        onRemove={onWithdraw}
        title={`Remove: ${volunteerText} from ${activityText}`}
        bottomText={`The reason for removal will be sent to the ${pluralize(
          'volunteer',
          selectedVolunteers.length
        )} after clicking the ’Remove’ button.`}
        warningText={`You are removing ${pluralize('this', selectedVolunteers.length)} ${pluralize(
          'person',
          selectedVolunteers.length
        )} from the waitlist`}
      />
      {teamExceed && maxTeamCancel.current && maxTeamConfirm.current && (
        <ConfirmAddMaxVolunteer onCancel={maxTeamCancel.current} onOK={maxTeamConfirm.current} teams={exceedingTeams} />
      )}
    </Box>
  );
}
